/**
 * Tipos compartilhados entre as entidades do Portal do Credenciado.
 *
 * Sem `docs/MODELO-DE-DADOS.md` para estas tabelas ainda (nenhuma existe no
 * banco): os campos aqui nascem do protótipo e do bom senso, não de migração.
 * Quando o módulo ganhar API real, este arquivo é o primeiro a se alinhar ao
 * schema publicado — mesmo papel que `operacao/comum/contrato.ts` já cumpre
 * para as entidades da operação.
 */

/** Envelope de página, no mesmo formato que a API real vai devolver. */
export interface Pagina<T> {
  itens: T[];
  total: number;
  pagina: number;
  tamanho: number;
}

export function paginar<T>(itens: readonly T[], pagina: number, tamanho: number): Pagina<T> {
  const inicio = (pagina - 1) * tamanho;
  return {
    itens: itens.slice(inicio, inicio + tamanho),
    total: itens.length,
    pagina,
    tamanho,
  };
}

/**
 * Por que o portal limita (ou só avisa sobre) a operação do credenciado —
 * mesmo desenho do `GatingStatus` do protótipo (`usePortalGatingStatus`),
 * fonte única de `GatingBanner` e de quem precisa desabilitar ação. O
 * vínculo pendente com cooperativa do protótipo não entra: a jornada não usa
 * esse vínculo (D-09).
 */
export type MotivoDaPendencia =
  | 'OK'
  | 'SUSPENSO'
  | 'INATIVO'
  | 'TERMO_NAO_ASSINADO'
  | 'SEM_TERMO_ATIVO'
  | 'CADIN_PENDENTE'
  | 'INSCRICAO_EM_ANALISE';

export interface SituacaoOperacionalDoPortal {
  motivo: MotivoDaPendencia;
  severidade: 'info' | 'warning' | 'destructive';
  titulo: string;
  detalhe: string;
  /** Atalho para resolver a pendência. Caminho dentro do portal. */
  cta?: { rotulo: string; caminho: string };
  /** `true` quando o motivo bloqueia ações operacionais — o CADIN só avisa. */
  bloqueia: boolean;
}

/**
 * Situação de um item do checklist, derivada como no protótipo
 * (`useDocumentosChecklist`): do `status_documento_upload` do envio mais
 * recente do tipo e da `validade_ate` dele. `vencido` é aprovado com validade
 * já passada; `pendente` é requisito sem envio nenhum.
 */
export type SituacaoDoChecklist =
  'aprovado' | 'enviado' | 'aprovacao_pendente' | 'pendente' | 'rejeitado' | 'vencido';

/**
 * Uma linha do `DocumentChecklist`: o requisito (`requisitos_documentais`) com
 * o envio mais recente dele (`documentos_upload`), quando houver.
 */
export interface ItemDoChecklist {
  id: string;
  /** `requisitos_documentais.nome_documento` */
  nome: string;
  descricao?: string | null;
  obrigatorio: boolean;
  ocrHabilitado?: boolean;
  situacao: SituacaoDoChecklist;
  /** Envio mais recente do tipo; ausente quando nada foi enviado. */
  documento?: { arquivoNome: string; validadeAte: string | null } | null;
  /**
   * Se o item aceita envio, quando quem decide é a API (tela de Documentos,
   * 0279: Pendente, Rejeitado e Vencido). Ausente, vale a regra do protótipo:
   * só Pendente e Rejeitado.
   */
  podeEnviar?: boolean;
}

/** Documento aprovado antes, oferecido no "Reutilizar" do mesmo tipo. */
export interface DocumentoReutilizavel {
  id: string;
  /** Mesmo `nome_documento` do requisito que ele pode atender. */
  tipo: string;
  arquivoNome: string;
  enviadoEm: string;
  validadeAte: string | null;
}
